import React, { useEffect, useId, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Combobox.css';

export interface ComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  ariaLabel?: string;
  id?: string;
}

/**
 * Accessible custom combobox with full visual control of the dropdown panel.
 * Replaces native <select> when you need to style the open menu (browsers
 * always render <select> popups using the OS theme).
 *
 * Keyboard support:
 * - Enter / Space / ArrowDown: open menu
 * - ArrowDown / ArrowUp: move highlight
 * - Home / End: jump to first / last
 * - Enter: commit highlighted option
 * - Escape: close without committing
 * - Type a letter: jump to next option starting with that letter
 */
const Combobox: React.FC<ComboboxProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  required = false,
  className = '',
  ariaLabel,
  id,
}) => {
  const generatedId = useId();
  const baseId = id || `cbx-${generatedId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [highlightIdx, setHighlightIdx] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeAheadRef = useRef<{ buffer: string; timer: number | null }>({
    buffer: '',
    timer: null,
  });

  const selectedIdx = options.findIndex((o) => o.value === value);
  const selected = selectedIdx >= 0 ? options[selectedIdx] : null;

  const open = useCallback(() => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setIsOpen(true);
    setHighlightIdx(selectedIdx >= 0 ? selectedIdx : 0);
  }, [disabled, selectedIdx]);

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlightIdx(-1);
    triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (idx: number) => {
      const opt = options[idx];
      if (!opt) return;
      onChange(opt.value);
      setIsOpen(false);
      setHighlightIdx(-1);
      triggerRef.current?.focus();
    },
    [onChange, options],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInsideTrigger = rootRef.current?.contains(target);
      const clickedInsideList = listRef.current?.contains(target);
      if (!clickedInsideTrigger && !clickedInsideList) {
        setIsOpen(false);
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || highlightIdx < 0) return;
    const li = listRef.current?.children[highlightIdx] as HTMLElement | undefined;
    li?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, isOpen]);

  // Close on scroll (outside the list), close on resize
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (e: Event) => {
      // Allow scrolling inside the dropdown list itself
      if (listRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
      setHighlightIdx(-1);
    };

    const handleResize = () => {
      setIsOpen(false);
      setHighlightIdx(-1);
    };

    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        open();
      }
      return;
    }
    handleListKeyDown(e);
  };

  const handleListKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement | HTMLUListElement>,
  ) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(options.length - 1, (i < 0 ? -1 : i) + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(0, (i < 0 ? options.length : i) - 1));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlightIdx(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setHighlightIdx(options.length - 1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0) commit(highlightIdx);
      return;
    }
    if (e.key === 'Tab') {
      setIsOpen(false);
      setHighlightIdx(-1);
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const ta = typeAheadRef.current;
      ta.buffer = (ta.buffer + e.key).toLowerCase();
      if (ta.timer) window.clearTimeout(ta.timer);
      ta.timer = window.setTimeout(() => {
        ta.buffer = '';
      }, 600);
      const startFrom = highlightIdx >= 0 ? highlightIdx : 0;
      const len = options.length;
      for (let step = 0; step < len; step++) {
        const idx = (startFrom + (ta.buffer.length === 1 ? step + 1 : step)) % len;
        if (options[idx].label.toLowerCase().startsWith(ta.buffer)) {
          setHighlightIdx(idx);
          break;
        }
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className={`combobox ${isOpen ? 'combobox--open' : ''} ${disabled ? 'combobox--disabled' : ''} ${className}`}
    >
      <button
        ref={triggerRef}
        type="button"
        id={baseId}
        role="combobox"
        className="combobox-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${baseId}-listbox`}
        aria-label={ariaLabel}
        aria-required={required}
        disabled={disabled}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={`combobox-value ${!selected ? 'combobox-value--placeholder' : ''}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className="combobox-chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && createPortal(
        <ul
          ref={listRef}
          id={`${baseId}-listbox`}
          role="listbox"
          tabIndex={-1}
          className="combobox-listbox"
          style={dropdownStyle}
          aria-labelledby={baseId}
          aria-activedescendant={
            highlightIdx >= 0 ? `${baseId}-opt-${highlightIdx}` : undefined
          }
          onKeyDown={handleListKeyDown}
        >
          {options.length === 0 && (
            <li className="combobox-empty">No options</li>
          )}
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            const isHighlighted = i === highlightIdx;
            return (
              <li
                key={opt.value || `opt-${i}`}
                id={`${baseId}-opt-${i}`}
                role="option"
                aria-selected={isSelected}
                className={`combobox-option ${isHighlighted ? 'combobox-option--highlighted' : ''} ${
                  isSelected ? 'combobox-option--selected' : ''
                }`}
                onMouseEnter={() => setHighlightIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(i);
                }}
              >
                <span className="combobox-option-label">{opt.label}</span>
                {opt.hint && <span className="combobox-option-hint">{opt.hint}</span>}
                {isSelected && (
                  <svg
                    className="combobox-option-check"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default Combobox;
